import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Container, Row, Col, Button, Form, Modal, Pagination, Dropdown, InputGroup, Spinner } from 'react-bootstrap';
import { 
  FaSearch, FaTimesCircle, FaShoppingBag, FaTruck, FaCheckCircle, 
  FaClock, FaCoins, FaFileInvoiceDollar, FaUser, FaPhoneAlt, 
  FaStore, FaGlobe, FaBox, FaSyncAlt, 
  FaMapMarkerAlt, FaPlus, FaFilePrescription, FaEye, FaBan,
  FaSearchPlus, FaSearchMinus, FaRedo, FaExclamationTriangle
} from 'react-icons/fa';
import ProgressDialog from './ProgressDialog';
import NoDataCard from './NoDataCard';
import { get, post, put, BASE_URL } from '../utils/api';
import { useToast } from './ToastContext';
import { CURRENCY_SYMBOL } from '../config';
import './OrderManagement.css';

const OrderManagement = () => {
  const { showToast } = useToast();
  const [orderList, setOrderList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters and sorting state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');
  const itemsPerPage = 9;

  // Modal and assignment states
  const [showModal, setShowModal] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [deliveryPersonSearchTerm, setDeliveryPersonSearchTerm] = useState('');
  const [filteredDeliveryPersons, setFilteredDeliveryPersons] = useState([]);
  const [delivery_person_id, setDeliveryPersonId] = useState('');
  const [realDeliveryPersons, setRealDeliveryPersons] = useState([]);
  const [assignedDeliveryPerson, setAssignedDeliveryPerson] = useState(null);
  const [assignedDeliveryPersonLoading, setAssignedDeliveryPersonLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
  const [orderDetailsError, setOrderDetailsError] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [assigningLoading, setAssigningLoading] = useState(false);

  // Prescription Zoom & Preview States
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  const [prescriptionImageUrl, setPrescriptionImageUrl] = useState('');
  const [prescriptionZoom, setPrescriptionZoom] = useState(1);
  const [prescriptionRotation, setPrescriptionRotation] = useState(0);

  // Item-level Rejection States
  const [showRejectItemModal, setShowRejectItemModal] = useState(false);
  const [itemToReject, setItemToReject] = useState(null);
  const [itemRejectionReason, setItemRejectionReason] = useState('');
  const [itemRejectingLoading, setItemRejectingLoading] = useState(false);

  // Order-level Rejection Reason
  const [orderRejectionReason, setOrderRejectionReason] = useState('');

  const fetchOrders = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setError(null);
    try {
      const ordersRes = await get('/orders?limit=10000');
      if (ordersRes && ordersRes.success) {
        setOrderList(ordersRes.data || []);
      } else {
        throw new Error(ordersRes?.message || 'Failed to fetch orders.');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch orders.');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  const fetchDeliveryPersons = useCallback(async () => {
    try {
      const response = await get('/delivery/personnel');
      if (response && response.success) {
        setRealDeliveryPersons(response.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch delivery persons:', err);
    }
  }, []);

  useEffect(() => {
    fetchOrders(true);
    fetchDeliveryPersons();
  }, [fetchOrders, fetchDeliveryPersons]);

  // Dynamic Statistics Calculations
  const stats = useMemo(() => {
    let totalOrders = orderList.length;
    let totalRevenue = 0;
    let inTransitCount = 0;
    let deliveredCount = 0;

    orderList.forEach(o => {
      totalRevenue += parseFloat(o.total_amount || 0);
      const st = String(o.status || '').toLowerCase();
      if (st === 'delivered') {
        deliveredCount++;
      } else if (st === 'pending' || st === 'confirmed' || st === 'dispatched') {
        inTransitCount++;
      }
    });

    return {
      totalOrders,
      totalRevenue: totalRevenue.toFixed(2),
      inTransitCount,
      deliveredCount
    };
  }, [orderList]);

  // Filtering & Sorting Logic
  const filteredAndSortedOrders = useMemo(() => {
    let list = [...orderList];

    // 1. Search Query Filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(o => {
        const orderIdStr = String(o.order_id || '').toLowerCase();
        const customerName = String(o.customer_name || '').toLowerCase();
        const customerMobile = String(o.customer_mobile || '').toLowerCase();
        const paymentMethod = String(o.payment_method || '').toLowerCase();
        const orderType = String(o.order_type || '').toLowerCase();
        return (
          orderIdStr.includes(q) ||
          customerName.includes(q) ||
          customerMobile.includes(q) ||
          paymentMethod.includes(q) ||
          orderType.includes(q)
        );
      });
    }

    // 2. Status Filter
    if (statusFilter !== 'all') {
      list = list.filter(o => String(o.status || '').toLowerCase() === statusFilter.toLowerCase());
    }

    // 3. Order Type Filter
    if (typeFilter !== 'all') {
      list = list.filter(o => String(o.order_type || '').toLowerCase() === typeFilter.toLowerCase());
    }

    // 4. Payment Status Filter
    if (paymentFilter !== 'all') {
      list = list.filter(o => String(o.payment_status || '').toLowerCase() === paymentFilter.toLowerCase());
    }

    // 5. Sorting
    list.sort((a, b) => {
      if (sortBy === 'date-desc') {
        return new Date(b.order_date) - new Date(a.order_date);
      }
      if (sortBy === 'date-asc') {
        return new Date(a.order_date) - new Date(b.order_date);
      }
      if (sortBy === 'amount-desc') {
        return (parseFloat(b.total_amount) || 0) - (parseFloat(a.total_amount) || 0);
      }
      if (sortBy === 'amount-asc') {
        return (parseFloat(a.total_amount) || 0) - (parseFloat(b.total_amount) || 0);
      }
      return 0;
    });

    return list;
  }, [orderList, searchQuery, statusFilter, typeFilter, paymentFilter, sortBy]);

  // Client-side pagination
  const totalPages = Math.ceil(filteredAndSortedOrders.length / itemsPerPage) || 1;
  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedOrders.slice(start, start + itemsPerPage);
  }, [filteredAndSortedOrders, currentPage, itemsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date)
      ? date.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
      : '';
  };

  const formatStatus = (status) => {
    if (!status) return '';
    return status.replace(/_/g, ' ').toUpperCase();
  };

  const handleShowModal = async (order) => {
    setCurrentOrder(order);
    setNewStatus(order.status);
    setOrderRejectionReason(order.rejection_reason || '');
    setDeliveryPersonSearchTerm('');
    setFilteredDeliveryPersons([]);
    setDeliveryPersonId('');
    setAssignedDeliveryPerson(null);
    setOrderDetails(null);
    setOrderDetailsError(null);
    setShowModal(true);

    // Fetch assigned delivery person only for online orders
    const isOnlineOrder = (order.order_type || '').toLowerCase() === 'online';
    if (isOnlineOrder) {
      setAssignedDeliveryPersonLoading(true);
      try {
        const response = await get(`/orders/${order.order_id}/delivery`);
        if (response && response.success && response.data) {
          setAssignedDeliveryPerson(response.data);
          setDeliveryPersonId(response.data.delivery_person_id);
          setDeliveryPersonSearchTerm(`${response.data.name} (${response.data.delivery_person_id})`);
        } else {
          setAssignedDeliveryPerson(null);
          setDeliveryPersonId('');
          setDeliveryPersonSearchTerm('');
        }
      } catch (err) {
        setAssignedDeliveryPerson(null);
        setDeliveryPersonId('');
        setDeliveryPersonSearchTerm('');
      } finally {
        setAssignedDeliveryPersonLoading(false);
      }
    } else {
      setAssignedDeliveryPerson(null);
      setDeliveryPersonId('');
      setDeliveryPersonSearchTerm('');
      setAssignedDeliveryPersonLoading(false);
    }

    // Fetch order details
    setOrderDetailsLoading(true);
    try {
      const response = await get(`/orders/${order.order_id}`);
      if (response && response.success) {
        setOrderDetails(response.data);
      } else {
        setOrderDetailsError(response?.message || 'Failed to fetch order details.');
      }
    } catch (err) {
      setOrderDetailsError(err.message || 'Failed to fetch order details.');
    } finally {
      setOrderDetailsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setAssignedDeliveryPerson(null);
    setAssignedDeliveryPersonLoading(false);
    setOrderDetails(null);
    setOrderDetailsLoading(false);
    setOrderDetailsError(null);
    setOrderRejectionReason('');
  };

  const handleViewPrescription = (url) => {
    if (!url) return;
    const fullUrl = url.startsWith('http') ? url : `${BASE_URL.replace(/\/api\/?$/, '')}${url}`;
    setPrescriptionImageUrl(fullUrl);
    setPrescriptionZoom(1);
    setPrescriptionRotation(0);
    setShowPrescriptionModal(true);
  };

  const handleOpenRejectItemModal = (item) => {
    setItemToReject(item);
    setItemRejectionReason('');
    setShowRejectItemModal(true);
  };

  const handleConfirmRejectItem = async () => {
    if (!itemRejectionReason.trim()) {
      showToast('Please enter a rejection reason.', 'error');
      return;
    }

    setItemRejectingLoading(true);
    try {
      const result = await put(`/orders/${currentOrder.order_id}/items/${itemToReject.order_item_id}/reject`, {
        rejection_reason: itemRejectionReason.trim()
      });

      if (result && result.success) {
        showToast('Item rejected and stock restored successfully.', 'success');
        setShowRejectItemModal(false);
        setItemToReject(null);
        setItemRejectionReason('');
        // Refresh current order details and list
        fetchOrders(false);
        if (currentOrder) {
          const refreshed = await get(`/orders/${currentOrder.order_id}`);
          if (refreshed && refreshed.success) {
            setOrderDetails(refreshed.data);
            setCurrentOrder(prev => ({
              ...prev,
              total_amount: refreshed.data.total_amount,
              status: refreshed.data.status,
              rejection_reason: refreshed.data.rejection_reason
            }));
          }
        }
      } else {
        showToast(result?.message || 'Failed to reject item.', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Failed to reject item.', 'error');
    } finally {
      setItemRejectingLoading(false);
    }
  };

  const handleDeliveryPersonSearch = (e) => {
    const term = e.target.value;
    setDeliveryPersonSearchTerm(term);
    const nameOnlyTerm = term.split('(')[0].trim();

    if (nameOnlyTerm.length > 0) {
      const filtered = realDeliveryPersons.filter((person) =>
        person.name.toLowerCase().includes(nameOnlyTerm.toLowerCase()) ||
        String(person.delivery_person_id).includes(nameOnlyTerm)
      );
      setFilteredDeliveryPersons(filtered);
    } else {
      setFilteredDeliveryPersons([]);
    }
  };

  const handleSelectDeliveryPerson = (person) => {
    setDeliveryPersonId(person.delivery_person_id);
    setDeliveryPersonSearchTerm(`${person.name} (${person.delivery_person_id})`);
    setFilteredDeliveryPersons([]);
  };

  const handleSubmitAssignDelivery = async (event) => {
    event.preventDefault();
    if (!delivery_person_id) {
      showToast('Please select a delivery person.', 'error');
      return;
    }

    const profile = await get('/auth/profile');
    if (!profile || !profile.success) {
      showToast('You must be logged in to perform this action.', 'error');
      return;
    }

    setAssigningLoading(true);
    try {
      const assignmentData = {
        order_id: currentOrder.order_id,
        delivery_person_id: delivery_person_id,
        assigned_by: profile.data.user_id,
      };

      const result = await post('/delivery/assign', assignmentData);
      showToast(result.message || 'Delivery assigned successfully', result.success ? 'success' : 'error');

      if (result.success) {
        fetchOrders(false);
        handleCloseModal();
      }
    } catch (error) {
      showToast(error.message || 'Failed to assign delivery person.', 'error');
    } finally {
      setAssigningLoading(false);
    }
  };

  const handleSubmitStatusUpdate = async (event) => {
    event.preventDefault();
    if (newStatus === 'cancelled' && !orderRejectionReason.trim()) {
      showToast('Please provide a cancellation / rejection reason.', 'error');
      return;
    }

    setStatusUpdating(true);
    try {
      const payload = {
        status: newStatus,
        rejection_reason: orderRejectionReason.trim() || undefined,
        prescription_verified: newStatus === 'confirmed'
      };

      const result = await post(`/orders/${currentOrder.order_id}/status`, payload, { method: 'PATCH' });

      if (result.success) {
        showToast(result.message || 'Order status updated successfully!', 'success');
        fetchOrders(false);
        const isRefunded = result.refund && result.refund.success;
        if (currentOrder) {
          setCurrentOrder(prev => ({
            ...prev,
            status: newStatus,
            rejection_reason: orderRejectionReason.trim() || prev.rejection_reason,
            prescription_verified: newStatus === 'confirmed' ? 1 : prev.prescription_verified,
            payment_status: isRefunded ? 'refunded' : prev.payment_status
          }));
        }
        setOrderDetails(prev => prev ? ({
          ...prev,
          status: newStatus,
          rejection_reason: orderRejectionReason.trim() || prev.rejection_reason,
          payment_status: isRefunded ? 'refunded' : prev.payment_status,
          remarks: (result.refund && result.refund.refund_id) 
            ? `Instant refund initiated: ${result.refund.refund_id} (${CURRENCY_SYMBOL}${result.refund.amount})` 
            : prev.remarks
        }) : prev);
      } else {
        showToast(result.message || 'Failed to update order status.', 'error');
      }
    } catch (error) {
      showToast(error.message || 'Failed to update order status.', 'error');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleDownloadInvoice = async (orderId) => {
    if (!orderId) return;
    setDownloadingInvoice(orderId);
    const token = localStorage.getItem('token');
    try {
      const storeName = localStorage.getItem('storeName') || '';
      const storePhone = localStorage.getItem('storePhone') || '';
      const storeEmail = localStorage.getItem('storeEmail') || '';
      const storeAddress = localStorage.getItem('storeAddress') || '';
      const params = new URLSearchParams();
      if (storeName) params.append('storeName', storeName);
      if (storePhone) params.append('storePhone', storePhone);
      if (storeEmail) params.append('storeEmail', storeEmail);
      if (storeAddress) params.append('storeAddress', storeAddress);
      const query = params.toString() ? `?${params.toString()}` : '';
      const url = `${BASE_URL}/v1/orders/${orderId}/bill/download${query}`;
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const resp = await fetch(url, { method: 'GET', headers });
      if (!resp.ok) throw new Error(`Failed to download invoice (status ${resp.status})`);
      const blob = await resp.blob();
      const contentDisposition = resp.headers.get('content-disposition') || '';
      let fileName = `invoice-${orderId}.pdf`;
      const match = contentDisposition.match(/filename\*=UTF-8''(.+)$|filename="?([^";]+)"?/);
      if (match) {
        fileName = decodeURIComponent(match[1] || match[2]);
      }
      const blobUrl = window.URL.createObjectURL(blob);
      const newWin = window.open(blobUrl, '_blank');
      if (!newWin) {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      showToast(err.message || 'Failed to download invoice', 'error');
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const getStatusBadgeClass = (status) => {
    const s = String(status || '').toLowerCase();
    switch (s) {
      case 'delivered': return 'status-badge-delivered';
      case 'dispatched': return 'status-badge-dispatched';
      case 'confirmed': return 'status-badge-confirmed';
      case 'pending': return 'status-badge-pending';
      case 'cancelled': return 'status-badge-cancelled';
      default: return 'status-badge-default';
    }
  };

  const getPaymentBadgeClass = (status) => {
    const s = String(status || '').toLowerCase();
    switch (s) {
      case 'paid':
      case 'success': return 'payment-badge-paid';
      case 'unpaid': return 'payment-badge-unpaid';
      case 'pending': return 'payment-badge-pending';
      case 'refunded': return 'payment-badge-refunded';
      case 'failed': return 'payment-badge-failed';
      default: return 'payment-badge-default';
    }
  };

  if (loading && orderList.length === 0) {
    return <ProgressDialog show={true} message="Loading orders..." />;
  }

  if (error && orderList.length === 0) {
    return (
      <Container fluid className="order-management-page px-4 px-sm-5 py-4">
        <div className="text-danger p-4 text-center">
          <h4>Error Loading Orders</h4>
          <p>{error}</p>
          <Button variant="primary" onClick={() => fetchOrders(true)}>Retry</Button>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="order-management-page px-4 px-sm-5">
      <ProgressDialog show={loading && orderList.length > 0} message="Refreshing orders..." />

      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-bold tracking-tight order-page-title mb-1">Order Management</h2>
          <p className="page-header-subtitle mb-0">Track live customer orders, update fulfillment statuses, and manage deliveries.</p>
        </div>
        <div className="d-flex gap-2">
          <Button 
            variant="outline-secondary" 
            onClick={() => fetchOrders(true)} 
            className="card-hover py-2 px-3 d-flex align-items-center gap-2 header-action-btn"
          >
            <FaSyncAlt /> Refresh
          </Button>
          <Button 
            variant="primary" 
            onClick={() => { window.location.href = '/offline-sell'; }} 
            className="card-hover py-2 px-3 d-flex align-items-center gap-2"
          >
            <FaPlus /> New POS Order
          </Button>
        </div>
      </div>

      {/* KPI Stats Row */}
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}>
          <div className="order-stat-card total-orders-card">
            <div className="stat-icon-wrapper"><FaShoppingBag /></div>
            <div className="stat-details">
              <span className="stat-value">{stats.totalOrders}</span>
              <span className="stat-label">Total Orders</span>
            </div>
          </div>
        </Col>
        <Col xs={6} md={3}>
          <div className="order-stat-card revenue-card">
            <div className="stat-icon-wrapper"><FaCoins /></div>
            <div className="stat-details">
              <span className="stat-value">{CURRENCY_SYMBOL}{stats.totalRevenue}</span>
              <span className="stat-label">Total Revenue</span>
            </div>
          </div>
        </Col>
        <Col xs={6} md={3}>
          <div className="order-stat-card transit-card">
            <div className="stat-icon-wrapper"><FaTruck /></div>
            <div className="stat-details">
              <span className="stat-value">{stats.inTransitCount}</span>
              <span className="stat-label">In-Transit / Processing</span>
            </div>
          </div>
        </Col>
        <Col xs={6} md={3}>
          <div className="order-stat-card completed-card">
            <div className="stat-icon-wrapper"><FaCheckCircle /></div>
            <div className="stat-details">
              <span className="stat-value">{stats.deliveredCount}</span>
              <span className="stat-label">Completed Deliveries</span>
            </div>
          </div>
        </Col>
      </Row>

      {/* Glassmorphic Control Bar */}
      <div className="order-control-bar mb-4 py-2 px-3 glassmorphic-panel">
        <Row className="g-2 align-items-center">
          <Col xs={12} lg={4}>
            <InputGroup className="search-input-group compact">
              <InputGroup.Text className="bg-transparent border-end-0 py-1 px-2">
                <FaSearch className="text-muted" style={{ fontSize: '0.85rem' }} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search order #, customer, phone..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="border-start-0 ps-0 py-1 search-input-field text-sm"
                style={{ fontSize: '0.85rem', height: '36px' }}
              />
              {searchQuery && (
                <Button 
                  variant="link" 
                  onClick={() => { setSearchQuery(''); setCurrentPage(1); }} 
                  className="clear-search-btn text-muted border-start-0 py-0 px-2"
                >
                  <FaTimesCircle style={{ fontSize: '0.85rem' }} />
                </Button>
              )}
            </InputGroup>
          </Col>

          <Col xs={6} sm={3} lg={2}>
            <Dropdown className="w-100">
              <Dropdown.Toggle variant="outline-secondary" className="filter-dropdown-toggle w-100 d-flex justify-content-between align-items-center text-sm">
                <span className="text-truncate">
                  {statusFilter === 'all' ? 'Status: All' : `Status: ${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}`}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="filter-dropdown-menu w-100">
                <Dropdown.Item onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}>All Statuses</Dropdown.Item>
                <Dropdown.Item onClick={() => { setStatusFilter('pending'); setCurrentPage(1); }}>Pending</Dropdown.Item>
                <Dropdown.Item onClick={() => { setStatusFilter('confirmed'); setCurrentPage(1); }}>Confirmed</Dropdown.Item>
                <Dropdown.Item onClick={() => { setStatusFilter('dispatched'); setCurrentPage(1); }}>Dispatched</Dropdown.Item>
                <Dropdown.Item onClick={() => { setStatusFilter('delivered'); setCurrentPage(1); }}>Delivered</Dropdown.Item>
                <Dropdown.Item onClick={() => { setStatusFilter('cancelled'); setCurrentPage(1); }}>Cancelled</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>

          <Col xs={6} sm={3} lg={2}>
            <Dropdown className="w-100">
              <Dropdown.Toggle variant="outline-secondary" className="filter-dropdown-toggle w-100 d-flex justify-content-between align-items-center text-sm">
                <span className="text-truncate">
                  {typeFilter === 'all' ? 'Channel: All' : typeFilter === 'online' ? 'Channel: Online' : 'Channel: In-Store'}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="filter-dropdown-menu w-100">
                <Dropdown.Item onClick={() => { setTypeFilter('all'); setCurrentPage(1); }}>All Channels</Dropdown.Item>
                <Dropdown.Item onClick={() => { setTypeFilter('online'); setCurrentPage(1); }}>Online Orders</Dropdown.Item>
                <Dropdown.Item onClick={() => { setTypeFilter('offline'); setCurrentPage(1); }}>In-Store (POS)</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>

          <Col xs={6} sm={3} lg={2}>
            <Dropdown className="w-100">
              <Dropdown.Toggle variant="outline-secondary" className="filter-dropdown-toggle w-100 d-flex justify-content-between align-items-center text-sm">
                <span className="text-truncate">
                  {paymentFilter === 'all' ? 'Payment: All' : `Payment: ${paymentFilter.charAt(0).toUpperCase() + paymentFilter.slice(1)}`}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="filter-dropdown-menu w-100">
                <Dropdown.Item onClick={() => { setPaymentFilter('all'); setCurrentPage(1); }}>All Payments</Dropdown.Item>
                <Dropdown.Item onClick={() => { setPaymentFilter('paid'); setCurrentPage(1); }}>Paid</Dropdown.Item>
                <Dropdown.Item onClick={() => { setPaymentFilter('unpaid'); setCurrentPage(1); }}>Unpaid</Dropdown.Item>
                <Dropdown.Item onClick={() => { setPaymentFilter('pending'); setCurrentPage(1); }}>Pending</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>

          <Col xs={6} sm={3} lg={2}>
            <Dropdown className="w-100">
              <Dropdown.Toggle variant="outline-secondary" className="filter-dropdown-toggle w-100 d-flex justify-content-between align-items-center text-sm">
                <span className="text-truncate">
                  {sortBy === 'date-asc' ? 'Sort: Oldest' : sortBy === 'amount-desc' ? 'Sort: Amount (High)' : sortBy === 'amount-asc' ? 'Sort: Amount (Low)' : 'Sort: Newest'}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="filter-dropdown-menu w-100">
                <Dropdown.Item onClick={() => { setSortBy('date-desc'); setCurrentPage(1); }}>Sort: Date (Newest)</Dropdown.Item>
                <Dropdown.Item onClick={() => { setSortBy('date-asc'); setCurrentPage(1); }}>Sort: Date (Oldest)</Dropdown.Item>
                <Dropdown.Item onClick={() => { setSortBy('amount-desc'); setCurrentPage(1); }}>Sort: Amount (High to Low)</Dropdown.Item>
                <Dropdown.Item onClick={() => { setSortBy('amount-asc'); setCurrentPage(1); }}>Sort: Amount (Low to High)</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>
      </div>

      {/* Main Order Grid */}
      <div className="order-grid">
        {paginatedOrders.length === 0 ? (
          <div className="grid-no-data py-5 text-center w-100">
            <NoDataCard
              title="No Orders Found"
              message="No orders matched your selected search filter criteria."
              icon="📦"
              actionButton={
                <Button 
                  variant="primary" 
                  onClick={() => { setSearchQuery(''); setStatusFilter('all'); setTypeFilter('all'); setPaymentFilter('all'); }} 
                  className="card-hover"
                >
                  Clear All Filters
                </Button>
              }
            />
          </div>
        ) : (
          paginatedOrders.map((order) => {
            const isOnline = (order.order_type || '').toLowerCase() === 'online';
            const statusBadgeClass = getStatusBadgeClass(order.status);
            const paymentBadgeClass = getPaymentBadgeClass(order.payment_status);

            return (
              <div className="modern-order-card card-hover" key={order.order_id}>
                {/* Header with Order ID and Channel */}
                <div className="order-card-top-row mb-3">
                  <div className="order-id-group">
                    <span className="order-id-pill">#{order.order_id}</span>
                    <span className={`channel-pill ${isOnline ? 'channel-online' : 'channel-store'}`}>
                      {isOnline ? <><FaGlobe className="me-1" /> Online</> : <><FaStore className="me-1" /> In-Store</>}
                    </span>
                  </div>
                  <div className="order-date-text">
                    <FaClock className="me-1" />
                    {formatDateTime(order.order_date)}
                  </div>
                </div>

                {/* Customer Details */}
                <div className="order-card-customer-row mb-3">
                  <div className="customer-avatar-circle">
                    <FaUser />
                  </div>
                  <div className="customer-details-block">
                    <div className="customer-name-text text-truncate" title={order.customer_name || 'Store Customer'}>
                      {order.customer_name || 'Store Customer'}
                    </div>
                    <div className="customer-contact-text">
                      <FaPhoneAlt className="me-1" style={{ fontSize: '0.72rem' }} />
                      {order.customer_mobile || order.customer_phone || 'Walk-in Customer'}
                    </div>
                  </div>
                </div>

                {/* Pricing and Items Count */}
                <div className="order-card-financials mb-3">
                  <div>
                    <div className="financial-label">Total Bill</div>
                    <div className="financial-amount">{CURRENCY_SYMBOL}{parseFloat(order.total_amount || 0).toFixed(2)}</div>
                  </div>
                  <div className="text-end">
                    <div className="financial-label">Payment Method</div>
                    <div className="payment-method-pill">
                      {(order.payment_method || 'CASH').toUpperCase()}
                    </div>
                  </div>
                </div>

                {/* Status Badges Row */}
                <div className="order-card-status-row mb-3">
                  <div className="status-pair">
                    <span className="status-caption">Fulfillment</span>
                    <span className={`badge-pill ${statusBadgeClass}`}>
                      {formatStatus(order.status)}
                    </span>
                  </div>
                  <div className="status-pair text-end">
                    <span className="status-caption">Payment</span>
                    <span className={`badge-pill ${paymentBadgeClass}`}>
                      {String(order.payment_status || 'PENDING').toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Card Action Buttons */}
                <div className="order-card-actions">
                  <Button 
                    size="sm" 
                    onClick={() => handleShowModal(order)} 
                    className="manage-order-btn card-hover"
                  >
                    <FaBox className="me-1" /> Manage Order
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline-primary" 
                    onClick={() => handleDownloadInvoice(order.order_id)} 
                    disabled={downloadingInvoice === order.order_id}
                    className="invoice-download-btn card-hover"
                  >
                    {downloadingInvoice === order.order_id ? (
                      <><Spinner animation="border" size="sm" className="me-1" /> Loading</>
                    ) : (
                      <><FaFileInvoiceDollar className="me-1" /> Invoice</>
                    )}
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Custom Pagination */}
      {filteredAndSortedOrders.length > 0 && totalPages > 1 && (
        <Pagination className="order-pagination justify-content-center mt-4">
          <Pagination.Prev 
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))} 
            disabled={currentPage === 1}
          />
          {[...Array(totalPages).keys()].map((number) => (
            <Pagination.Item
              key={number + 1}
              active={number + 1 === currentPage}
              onClick={() => handlePageChange(number + 1)}
            >
              {number + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next 
            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} 
            disabled={currentPage === totalPages}
          />
        </Pagination>
      )}

      {/* Order Management & Details Modal */}
      <Modal 
        show={showModal} 
        onHide={handleCloseModal} 
        size="lg" 
        scrollable 
        className="order-modal" 
        dialogClassName="order-modal-dialog-lg"
        backdropClassName="order-modal-backdrop"
      >
        <Modal.Header closeButton className="order-modal-header py-3 px-4">
          <div className="d-flex align-items-center justify-content-between w-100 pe-3 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <div className="order-modal-icon-badge">
                <FaBox />
              </div>
              <div>
                <Modal.Title className="modal-order-title d-flex align-items-center gap-2 mb-0">
                  <span>Order #{currentOrder?.order_id}</span>
                </Modal.Title>
                <div className="modal-subtitle">
                  <span className="fw-semibold">{currentOrder?.customer_name || 'Walk-in Customer'}</span>
                  {(currentOrder?.customer_mobile || currentOrder?.customer_phone) && (
                    <span className="ms-2">({currentOrder.customer_mobile || currentOrder.customer_phone})</span>
                  )}
                  <span className="mx-2">•</span>
                  <span>{formatDateTime(currentOrder?.order_date)}</span>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className={`channel-pill ${((currentOrder?.order_type || '').toLowerCase() === 'online') ? 'channel-online' : 'channel-store'}`}>
                {((currentOrder?.order_type || '').toLowerCase() === 'online') ? <><FaGlobe className="me-1" /> Online</> : <><FaStore className="me-1" /> In-Store</>}
              </span>
              <span className={`badge-pill ${getStatusBadgeClass(currentOrder?.status)}`}>
                {formatStatus(currentOrder?.status)}
              </span>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="order-modal-body p-4">
          <div className="order-modal-layout">
            {/* Left Column: Order Items and Summary */}
            <div className="order-modal-left">
              {/* Prescription Card if attached */}
              {(orderDetails?.prescription_image_url || currentOrder?.prescription_image_url) && (
                <div className="order-prescription-card p-3 rounded mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="section-title mb-0 d-flex align-items-center gap-2">
                      <FaFilePrescription className="text-warning" /> Attached Prescription
                    </h6>
                    <span className={`badge-pill ${
                      (orderDetails?.prescription_status || currentOrder?.prescription_status) === 'approved' 
                        ? 'badge-status-completed' 
                        : (orderDetails?.prescription_status || currentOrder?.prescription_status) === 'rejected' 
                        ? 'badge-status-cancelled' 
                        : 'badge-status-transit'
                    }`}>
                      {formatStatus(orderDetails?.prescription_status || currentOrder?.prescription_status || 'Pending Verification')}
                    </span>
                  </div>
                  
                  <div className="d-flex gap-3 align-items-center mt-2 flex-wrap">
                    <div 
                      className="prescription-thumbnail-wrapper"
                      onClick={() => handleViewPrescription(orderDetails?.prescription_image_url || currentOrder?.prescription_image_url)}
                      style={{ cursor: 'pointer' }}
                    >
                      <img 
                        src={(orderDetails?.prescription_image_url || currentOrder?.prescription_image_url).startsWith('http') 
                          ? (orderDetails?.prescription_image_url || currentOrder?.prescription_image_url) 
                          : `${BASE_URL.replace(/\/api\/?$/, '')}${orderDetails?.prescription_image_url || currentOrder?.prescription_image_url}`} 
                        alt="Prescription" 
                        className="prescription-thumbnail rounded border"
                        style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                      />
                      <div className="thumbnail-zoom-overlay">
                        <FaEye />
                      </div>
                    </div>

                    <div className="prescription-meta-details flex-grow-1">
                      {orderDetails?.doctor_name && (
                        <div className="small fw-semibold doctor-name-text">Dr. {orderDetails.doctor_name}</div>
                      )}
                      {orderDetails?.doctor_phone && (
                        <div className="small text-muted">Contact: {orderDetails.doctor_phone}</div>
                      )}
                      {orderDetails?.prescription_remarks && (
                        <div className="small text-warning mt-1">Note: {orderDetails.prescription_remarks}</div>
                      )}
                      <div className="mt-2">
                        <Button 
                          variant="outline-warning" 
                          size="sm"
                          onClick={() => handleViewPrescription(orderDetails?.prescription_image_url || currentOrder?.prescription_image_url)}
                          className="d-inline-flex align-items-center gap-1"
                        >
                          <FaEye /> View Full Prescription
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Order Rejection Banner if rejected */}
              {(orderDetails?.rejection_reason || currentOrder?.rejection_reason) && (
                <div className="alert alert-danger d-flex align-items-center gap-2 mb-3 py-2 px-3">
                  <FaExclamationTriangle className="text-danger flex-shrink-0" />
                  <div>
                    <strong>Rejection/Cancellation Notice:</strong> {orderDetails?.rejection_reason || currentOrder?.rejection_reason}
                  </div>
                </div>
              )}

              <div className="order-items-card p-3 rounded mb-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="section-title mb-0 d-flex align-items-center gap-2">
                    <FaShoppingBag className="text-primary" /> Ordered Items
                  </h6>
                  {orderDetails?.items && (
                    <span className="items-count-badge">
                      {orderDetails.items.length} {orderDetails.items.length === 1 ? 'item' : 'items'}
                    </span>
                  )}
                </div>

                {orderDetailsLoading ? (
                  <div className="text-center py-4 modal-loading-text">
                    <Spinner animation="border" size="sm" className="me-2" /> Loading ordered items...
                  </div>
                ) : orderDetailsError ? (
                  <div className="text-danger small mb-2 p-2 rounded bg-danger-subtle">{orderDetailsError}</div>
                ) : orderDetails ? (
                  <div className="ordered-items-list">
                    {orderDetails.items && orderDetails.items.length > 0 ? (
                      orderDetails.items.map((item, index) => {
                        const categoryName = (item.categories && item.categories.length)
                          ? item.categories[0].name
                          : (item.category || 'Medicine');
                        const qty = parseFloat(item.quantity) || 1;
                        const unitPrice = parseFloat(item.unit_price) || (parseFloat(item.subtotal) ? parseFloat(item.subtotal) / qty : 0);
                        const subtotal = parseFloat(item.subtotal) || (unitPrice * qty) || 0;
                        const isRejected = item.item_status === 'rejected';

                        return (
                          <div key={index} className={`ordered-item-row ${isRejected ? 'item-rejected-row' : ''}`}>
                            <div className="item-info">
                              <div className="item-name d-flex align-items-center flex-wrap gap-2">
                                <span className={isRejected ? 'text-decoration-line-through text-muted' : ''}>
                                  {item.medicine_name}
                                </span>
                                <span className="item-qty-badge">Qty: {qty} {item.selected_unit || ''}</span>
                                {item.prescription_required ? (
                                  <span className="badge bg-warning-subtle text-warning border border-warning-subtle px-1" style={{ fontSize: '0.65rem' }}>
                                    Rx Required
                                  </span>
                                ) : null}
                                {isRejected && (
                                  <span className="badge bg-danger text-white" style={{ fontSize: '0.68rem' }}>
                                    Rejected
                                  </span>
                                )}
                              </div>
                              <div className="item-meta-line">
                                <span className="item-category-tag">{categoryName}</span>
                                <span className="item-meta-separator">•</span>
                                <span className="item-unit-price">Unit: {CURRENCY_SYMBOL}{unitPrice.toFixed(2)}</span>
                                {item.batch_number && (
                                  <>
                                    <span className="item-meta-separator">•</span>
                                    <span className="item-batch-info text-muted">Batch: {item.batch_number}</span>
                                  </>
                                )}
                              </div>
                              {isRejected && item.rejection_reason && (
                                <div className="text-danger small mt-1">
                                  <strong>Reason:</strong> {item.rejection_reason}
                                </div>
                              )}
                            </div>

                            <div className="d-flex align-items-center gap-3">
                              <div className={`item-subtotal-value ${isRejected ? 'text-decoration-line-through text-muted' : ''}`}>
                                {CURRENCY_SYMBOL}{subtotal.toFixed(2)}
                              </div>
                              {!isRejected && currentOrder?.status !== 'cancelled' && currentOrder?.status !== 'delivered' && (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  className="btn-reject-item py-1 px-2"
                                  onClick={() => handleOpenRejectItemModal(item)}
                                  title="Reject this medicine from order"
                                >
                                  <FaBan className="me-1" /> Reject
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="no-items-placeholder py-3 text-center">No items found for this order.</div>
                    )}
                  </div>
                ) : null}
              </div>

              {/* Comprehensive Payment & Price Breakdown */}
              {orderDetails && (
                <div className="order-summary-box p-3 rounded mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom border-secondary border-opacity-25">
                    <span className="fw-bold d-flex align-items-center gap-2 text-primary" style={{ fontSize: '0.95rem' }}>
                      <FaFileInvoiceDollar /> Payment & Price Breakdown
                    </span>
                    <span className={`summary-payment-badge ${getPaymentBadgeClass(orderDetails.payment_status || 'pending')}`}>
                      {String(orderDetails.payment_status || 'Pending').toUpperCase()}
                    </span>
                  </div>

                  {/* Channel, Gateway & Transaction Details */}
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="summary-field-label">Order Channel:</span>
                    <span className="summary-field-value">
                      {orderDetails.order_type === 'online' ? '🌐 Online Order' : '🏪 In-Store (POS)'}
                    </span>
                  </div>

                  {orderDetails.payment_method && (
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="summary-field-label">Payment Method:</span>
                      <span className="summary-field-value text-uppercase">
                        {orderDetails.payment_method === 'online'
                          ? (orderDetails.gateway_name ? `Online (${orderDetails.gateway_name.toUpperCase()})` : 'Online Payment')
                          : orderDetails.payment_method}
                      </span>
                    </div>
                  )}

                  {orderDetails.transaction_id && (
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="summary-field-label">Transaction / Order ID:</span>
                      <code className="px-2 py-0.5 rounded bg-dark text-info small" style={{ fontSize: '0.78rem' }}>
                        {orderDetails.transaction_id}
                      </code>
                    </div>
                  )}

                  <hr className="my-2 summary-box-divider" />

                  {/* Itemized Price Breakdown */}
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="summary-field-label">Items Subtotal:</span>
                    <span className="summary-field-value">
                      {CURRENCY_SYMBOL}{parseFloat(orderDetails.subtotal_amount || 0).toFixed(2)}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="summary-field-label">Estimated Tax (GST):</span>
                    <span className="summary-field-value">
                      {CURRENCY_SYMBOL}{parseFloat(orderDetails.total_tax_amount || 0).toFixed(2)}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="summary-field-label">Delivery Fee:</span>
                    <span className="summary-field-value">
                      {Number(orderDetails.delivery_charge || 0) > 0 ? (
                        `${CURRENCY_SYMBOL}${parseFloat(orderDetails.delivery_charge).toFixed(2)}`
                      ) : (
                        <span className="text-success fw-bold">FREE</span>
                      )}
                    </span>
                  </div>

                  {Number(orderDetails.discount_amount || 0) > 0 && (
                    <div className="d-flex justify-content-between align-items-center mb-1 text-success">
                      <span className="summary-field-label text-success">Discount / Coupon:</span>
                      <span className="summary-field-value text-success">
                        - {CURRENCY_SYMBOL}{parseFloat(orderDetails.discount_amount).toFixed(2)}
                      </span>
                    </div>
                  )}

                  <hr className="my-2 summary-box-divider" />

                  <div className="d-flex justify-content-between align-items-center pt-1">
                    <span className="summary-total-label">Grand Total:</span>
                    <span className="summary-total-value text-primary">
                      {CURRENCY_SYMBOL}{parseFloat(orderDetails.total_amount || currentOrder?.total_amount || 0).toFixed(2)}
                    </span>
                  </div>

                  {/* Refund Notice if order is refunded or remarks mention refund */}
                  {(orderDetails.payment_status === 'refunded' || (orderDetails.remarks && orderDetails.remarks.toLowerCase().includes('refund'))) && (
                    <div className="mt-3 p-2 rounded border border-warning border-opacity-50" style={{ background: 'rgba(168, 85, 247, 0.12)' }}>
                      <div className="d-flex align-items-center gap-2 fw-semibold text-warning small">
                        <FaCheckCircle className="text-success" /> Refund Processed
                      </div>
                      <div className="small text-white-50 mt-1" style={{ fontSize: '0.78rem' }}>
                        {orderDetails.remarks || 'Full amount has been refunded to the customer.'}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right Column: Status Update, Address, and Delivery Assignment */}
            <div className="order-modal-right">
              {/* Status Update Form */}
              <div className="status-update-panel p-3 rounded mb-3">
                <h6 className="section-title mb-3 d-flex align-items-center gap-2">
                  <FaSyncAlt className="text-warning" /> Fulfillment & Verification
                </h6>
                <Form onSubmit={handleSubmitStatusUpdate}>
                  <Form.Group className="mb-3">
                    <Form.Label className="status-form-label mb-1">Update Current Status</Form.Label>
                    <Form.Select 
                      value={newStatus} 
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="status-select-input"
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed / Prescription Verified</option>
                      <option value="dispatched">Dispatched</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled / Rejected</option>
                    </Form.Select>
                  </Form.Group>

                  {newStatus === 'cancelled' && (orderDetails?.payment_status === 'paid' || currentOrder?.payment_status === 'paid') && (
                    <div className="alert alert-warning py-2 px-3 small mb-3 border-warning d-flex align-items-start gap-2" style={{ background: 'rgba(234, 179, 8, 0.14)' }}>
                      <FaExclamationTriangle className="mt-1 flex-shrink-0 text-warning" />
                      <div>
                        <strong>Instant Refund Notice:</strong> This order is marked as <strong>PAID</strong>. Changing status to Cancelled / Rejected will immediately issue a full refund of <strong>{CURRENCY_SYMBOL}{parseFloat(orderDetails?.total_amount || currentOrder?.total_amount || 0).toFixed(2)}</strong> via Razorpay to the customer's original payment method.
                      </div>
                    </div>
                  )}

                  {newStatus === 'cancelled' && (
                    <Form.Group className="mb-3">
                      <Form.Label className="status-form-label mb-1 text-danger">Cancellation / Rejection Reason *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="State reason (e.g. Invalid prescription, medicine out of stock)"
                        value={orderRejectionReason}
                        onChange={(e) => setOrderRejectionReason(e.target.value)}
                        className="bg-dark text-white border-secondary"
                        required
                      />
                    </Form.Group>
                  )}

                  <Button 
                    variant={newStatus === 'cancelled' ? 'danger' : 'primary'} 
                    type="submit" 
                    className="w-100 py-2 d-flex align-items-center justify-content-center gap-2" 
                    disabled={statusUpdating}
                  >
                    {statusUpdating ? <><Spinner animation="border" size="sm" /> Updating...</> : 'Save Status'}
                  </Button>
                </Form>
              </div>

              {/* Delivery Address if online order */}
              {currentOrder?.delivery_address && (
                <div className="delivery-address-card p-3 rounded mb-3">
                  <div className="address-header mb-2 fw-semibold d-flex align-items-center gap-2">
                    <FaMapMarkerAlt className="text-danger" /> Delivery Address
                  </div>
                  <div className="address-text">
                    <div>{currentOrder.delivery_address.address_line1}</div>
                    {currentOrder.delivery_address.address_line2 && <div>{currentOrder.delivery_address.address_line2}</div>}
                    <div>{currentOrder.delivery_address.city}, {currentOrder.delivery_address.state} - {currentOrder.delivery_address.postal_code}</div>
                  </div>
                </div>
              )}

              {/* In-Store Sale Information for Offline Orders */}
              {((currentOrder?.order_type || '').toLowerCase() === 'offline') && (
                <div className="p-3 rounded mb-3 pos-sale-callout">
                  <div className="fw-semibold mb-1 d-flex align-items-center gap-2 text-warning">
                    <FaStore /> In-Store (POS) Sale
                  </div>
                  <div className="small text-muted">
                    This order was created and completed directly at the physical store counter. Delivery assignment is not required.
                  </div>
                </div>
              )}

              {/* Delivery Assignment Form (for online orders) */}
              {(currentOrder && (currentOrder.order_type || '').toLowerCase() !== 'offline') && (
                <div className="delivery-assign-panel p-3 rounded">
                  <h6 className="section-title mb-2 d-flex align-items-center gap-2">
                    <FaTruck className="text-info" /> Delivery Personnel
                  </h6>
                  
                  {assignedDeliveryPersonLoading ? (
                    <div className="modal-loading-text py-2">Loading assigned agent...</div>
                  ) : assignedDeliveryPerson ? (
                    <div className="assigned-agent-pill mb-3 p-2 rounded">
                      <div className="agent-name agent-name-text fw-bold small">{assignedDeliveryPerson.name}</div>
                      <div className="agent-phone">{assignedDeliveryPerson.phone || 'No phone'}</div>
                    </div>
                  ) : (
                    <div className="text-warning small mb-2">No delivery personnel assigned yet.</div>
                  )}

                  <Form onSubmit={handleSubmitAssignDelivery}>
                    <Form.Group className="mb-2 position-relative">
                      <Form.Control
                        type="text"
                        placeholder="Search delivery personnel..."
                        value={deliveryPersonSearchTerm}
                        onChange={handleDeliveryPersonSearch}
                        className="delivery-search-input"
                        required
                      />
                      {deliveryPersonSearchTerm && filteredDeliveryPersons.length > 0 && (
                        <div className="delivery-suggestions-dropdown">
                          {filteredDeliveryPersons.map((person) => (
                            <div
                              key={person.delivery_person_id}
                              className="delivery-suggestion-item"
                              onClick={() => handleSelectDeliveryPerson(person)}
                            >
                              <div className="fw-semibold agent-suggestion-name">{person.name}</div>
                              <div className="suggestion-meta">ID: #{person.delivery_person_id} • {person.phone || 'N/A'}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </Form.Group>
                    <Button 
                      variant="success" 
                      type="submit" 
                      className="w-100 py-2 d-flex align-items-center justify-content-center gap-2" 
                      disabled={assigningLoading}
                    >
                      {assigningLoading ? <><Spinner animation="border" size="sm" /> Assigning...</> : 'Assign Delivery Person'}
                    </Button>
                  </Form>
                </div>
              )}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="order-modal-footer d-flex justify-content-between align-items-center py-2 px-4">
          <Button 
            variant="outline-primary" 
            size="sm" 
            onClick={() => handleDownloadInvoice(currentOrder?.order_id)} 
            disabled={downloadingInvoice === currentOrder?.order_id}
            className="d-flex align-items-center gap-2"
          >
            {downloadingInvoice === currentOrder?.order_id ? (
              <><Spinner animation="border" size="sm" /> Generating Invoice...</>
            ) : (
              <><FaFileInvoiceDollar /> Download Invoice PDF</>
            )}
          </Button>
          <Button variant="secondary" size="sm" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Prescription Full Screen & Zoom Modal */}
      <Modal
        show={showPrescriptionModal}
        onHide={() => setShowPrescriptionModal(false)}
        size="lg"
        centered
        className="prescription-modal"
      >
        <Modal.Header closeButton className="order-submodal-header">
          <Modal.Title className="h6 d-flex align-items-center gap-2 mb-0">
            <FaFilePrescription className="text-warning" /> Customer Prescription
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-black text-center p-3 position-relative" style={{ minHeight: '400px', overflow: 'auto' }}>
          <div className="d-flex justify-content-center gap-2 mb-3">
            <Button 
              variant="secondary" 
              size="sm" 
              onClick={() => setPrescriptionZoom(prev => Math.min(prev + 0.25, 3))}
              title="Zoom In"
            >
              <FaSearchPlus /> Zoom In
            </Button>
            <Button 
              variant="secondary" 
              size="sm" 
              onClick={() => setPrescriptionZoom(prev => Math.max(prev - 0.25, 0.5))}
              title="Zoom Out"
            >
              <FaSearchMinus /> Zoom Out
            </Button>
            <Button 
              variant="secondary" 
              size="sm" 
              onClick={() => setPrescriptionRotation(prev => (prev + 90) % 360)}
              title="Rotate"
            >
              <FaRedo /> Rotate
            </Button>
            <Button 
              variant="outline-light" 
              size="sm" 
              onClick={() => { setPrescriptionZoom(1); setPrescriptionRotation(0); }}
            >
              Reset
            </Button>
          </div>
          <div style={{ overflow: 'auto', maxHeight: '65vh' }}>
            <img 
              src={prescriptionImageUrl} 
              alt="Prescription Full" 
              className="img-fluid rounded"
              style={{ 
                transform: `scale(${prescriptionZoom}) rotate(${prescriptionRotation}deg)`,
                transition: 'transform 0.2s ease-in-out',
                maxWidth: '100%'
              }} 
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-dark border-secondary">
          <Button variant="secondary" size="sm" onClick={() => setShowPrescriptionModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reject Order Item Confirmation Modal */}
      <Modal
        show={showRejectItemModal}
        onHide={() => !itemRejectingLoading && setShowRejectItemModal(false)}
        centered
        className="reject-item-modal"
      >
        <Modal.Header closeButton className="order-submodal-header">
          <Modal.Title className="h6 text-danger d-flex align-items-center gap-2 mb-0">
            <FaBan /> Reject Medicine from Order
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="order-submodal-body">
          <p className="mb-2">
            Are you sure you want to reject <strong>{itemToReject?.medicine_name}</strong> (Qty: {itemToReject?.quantity})?
          </p>
          <div className="alert alert-warning py-2 small mb-3">
            Inventory stock will automatically be returned to the batch and the order total will be recalculated.
          </div>
          <Form.Group>
            <Form.Label className="small fw-semibold">Rejection Reason *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="e.g. Not mentioned on uploaded prescription, dosage unclear, out of stock"
              value={itemRejectionReason}
              onChange={(e) => setItemRejectionReason(e.target.value)}
              className="order-submodal-input"
              required
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="bg-dark border-secondary">
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={() => setShowRejectItemModal(false)}
            disabled={itemRejectingLoading}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            size="sm" 
            onClick={handleConfirmRejectItem}
            disabled={itemRejectingLoading || !itemRejectionReason.trim()}
          >
            {itemRejectingLoading ? <><Spinner animation="border" size="sm" className="me-1" /> Rejecting...</> : 'Confirm Item Rejection'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default OrderManagement;
